import { ArrowLeftRight, ShoppingCart, Link2, Split, Send, RefreshCw, ShieldCheck, Network } from "lucide-react";

const CATEGORY = "Zahlungen";

export const PAYMENTS = [
  {
    slug: "zahlungen",
    name: "Zahlungen",
    category: CATEGORY,
    icon: ArrowLeftRight,
    tagline: "Ein- und ausgehende Zahlungen über eine Engine.",
    overline: "Zahlungen",
    hero: {
      title: "Eine Engine für jeden Zahlungsfluss.",
      subtitle:
        "Orchestrieren Sie ein- und ausgehende Zahlungen über SEPA, SCT Instant und SWIFT – deterministisch, nachvollziehbar und über eine einzige API.",
    },
    stats: [
      { value: "SEPA", label: "SCT & Instant" },
      { value: "SWIFT", label: "international" },
      { value: "Idempotent", label: "sicher bei Retry" },
    ],
    features: [
      { icon: ArrowLeftRight, title: "Ein- & ausgehend", desc: "Collections und Disbursements über dieselbe konsistente Schnittstelle." },
      { icon: Network, title: "Multi-Rail", desc: "SEPA SCT, SCT Instant und SWIFT – die Engine wählt den passenden Rail." },
      { icon: ShieldCheck, title: "Idempotenz", desc: "Idempotency-Keys verhindern Doppelzahlungen bei Netzwerk-Retries." },
      { icon: RefreshCw, title: "Statuslebenszyklus", desc: "Klar definierte Zustände von created bis settled, jederzeit per Webhook." },
    ],
    architecture: {
      caption: "Deterministische Payment-Engine über mehreren Rails",
      layers: [
        { label: "Initiierung", nodes: [{ label: "API", sub: "v1" }, { label: "Dashboard", sub: "manuell" }, { label: "Batch", sub: "Datei" }] },
        { label: "Payment-Engine", nodes: [{ label: "Routing", sub: "Rail-Wahl" }, { label: "Screening", sub: "AML" }, { label: "Ledger", sub: "Buchung" }] },
        { label: "Rails", nodes: [{ label: "SEPA", sub: "SCT" }, { label: "Instant", sub: "10s" }, { label: "SWIFT", sub: "MT" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/payments",
      code: [
        "// Ausgehende Zahlung initiieren",
        "const payment = await cashfux.payments.create({",
        '  from: "acc_9f2",',
        '  amount: 149900, // 1.499,00 €',
        '  currency: "EUR",',
        '  scheme: "sepa_instant",',
        '  creditor: { iban: "DE22...", name: "Lieferant AG" },',
        "});",
      ],
      highlights: [
        "Rail-Auswahl automatisch oder explizit",
        "Idempotency-Key gegen Doppelbuchung",
        "Webhooks für jeden Statuswechsel",
      ],
    },
    useCases: [
      { title: "Lieferantenzahlungen", desc: "Rechnungen automatisiert und terminiert begleichen." },
      { title: "Instant-Erstattungen", desc: "Rückzahlungen in Sekunden über SCT Instant." },
      { title: "Cross-Border", desc: "Internationale Zahlungen via SWIFT aus derselben API." },
    ],
    faq: [
      { q: "Welche Zahlungsschemata werden unterstützt?", a: "SEPA SCT, SEPA SCT Instant und SWIFT – wählbar oder automatisch geroutet." },
      { q: "Wie werden Doppelzahlungen verhindert?", a: "Über Idempotency-Keys: identische Requests erzeugen niemals zwei Zahlungen." },
      { q: "Wie erfahre ich vom Zahlungsstatus?", a: "Über Webhooks und Statusabfragen entlang des definierten Lebenszyklus." },
      { q: "Sind Batch-Zahlungen möglich?", a: "Ja, über Batch-Uploads oder gebündelte API-Aufrufe mit Sammelfreigabe." },
    ],
    related: ["checkout", "auszahlungen", "split-payments"],
  },
  {
    slug: "checkout",
    name: "Checkout",
    category: CATEGORY,
    icon: ShoppingCart,
    tagline: "Fertiger, einbettbarer Bezahlvorgang.",
    overline: "Checkout",
    hero: {
      title: "Bezahlen, eingebettet in Ihr Produkt.",
      subtitle:
        "Ein konversionsstarker, White-Label-Checkout mit den relevanten Zahlungsmethoden – als gehostete Seite oder eingebettet, in Minuten integriert.",
    },
    stats: [
      { value: "White Label", label: "Ihre Marke" },
      { value: "1 Klick", label: "Wiederkäufer" },
      { value: "3DS2", label: "SCA-konform" },
    ],
    features: [
      { icon: ShoppingCart, title: "Gehostet & eingebettet", desc: "Nutzen Sie eine gehostete Checkout-Seite oder betten Sie die Felder direkt ein." },
      { icon: ShieldCheck, title: "SCA & 3-D Secure 2", desc: "Starke Kundenauthentifizierung integriert, ohne Konversionsverlust." },
      { icon: RefreshCw, title: "Methoden-Mix", desc: "SEPA-Lastschrift, Karten und Wallet-Zahlungen in einem Flow." },
      { icon: Network, title: "Session-API", desc: "Checkout-Sessions serverseitig erstellen und clientseitig sicher einlösen." },
    ],
    architecture: {
      caption: "Checkout-Session vom Server bis zum Ledger",
      layers: [
        { label: "Client", nodes: [{ label: "Hosted Page", sub: "URL" }, { label: "Embedded", sub: "SDK" }] },
        { label: "Session-Layer", nodes: [{ label: "Session", sub: "Token" }, { label: "3DS2", sub: "SCA" }, { label: "Methoden", sub: "Auswahl" }] },
        { label: "Verarbeitung", nodes: [{ label: "Payment", sub: "capture" }, { label: "Ledger", sub: "Buchung" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/checkout/sessions",
      code: [
        "// Checkout-Session serverseitig erstellen",
        "const session = await cashfux.checkout.sessions.create({",
        "  amount: 4990,",
        '  currency: "EUR",',
        '  methods: ["card", "sepa_debit"],',
        '  success_url: "https://shop.de/ok",',
        "});",
        "",
        "session.url // an den Kunden weiterleiten",
      ],
      highlights: [
        "Session-Secret niemals im Client",
        "Automatisches 3DS2 bei Kartenzahlung",
        "Webhook checkout.completed",
      ],
    },
    useCases: [
      { title: "Onlineshops", desc: "Konversionsstarker Checkout unter eigener Marke." },
      { title: "SaaS-Upgrades", desc: "In-App-Bezahlvorgang ohne Redirect-Bruch." },
      { title: "Event-Ticketing", desc: "Schneller Kauf mit den passenden lokalen Methoden." },
    ],
    faq: [
      { q: "Gehostet oder eingebettet – was empfiehlt sich?", a: "Gehostet ist am schnellsten live; eingebettet gibt volle Kontrolle über die UI. Beides ist White-Label." },
      { q: "Ist die Lösung SCA-konform?", a: "Ja, 3-D Secure 2 und starke Kundenauthentifizierung sind integriert." },
      { q: "Welche Zahlungsmethoden sind verfügbar?", a: "Karten, SEPA-Lastschrift und Wallet-Zahlungen – kombinierbar pro Session." },
      { q: "Wie sichere ich den Abschluss serverseitig ab?", a: "Über den Webhook checkout.completed und eine serverseitige Verifikation der Session." },
    ],
    related: ["payment-links", "zahlungen", "subscriptions"],
  },
  {
    slug: "payment-links",
    name: "Payment Links",
    category: CATEGORY,
    icon: Link2,
    tagline: "Bezahllinks ohne eine Zeile Code.",
    overline: "Payment Links",
    hero: {
      title: "Kassieren mit einem Link.",
      subtitle:
        "Erstellen Sie teilbare Bezahllinks per API oder Dashboard – ideal für Rechnungen, Anzahlungen und Verkäufe ohne eigenen Checkout.",
    },
    stats: [
      { value: "0 Code", label: "sofort teilbar" },
      { value: "QR", label: "vor Ort" },
      { value: "Multi-Use", label: "oder Einmal" },
    ],
    features: [
      { icon: Link2, title: "Sofort teilbar", desc: "Link per E-Mail, Chat oder QR-Code versenden – kein Frontend nötig." },
      { icon: RefreshCw, title: "Einmal oder wiederholt", desc: "Links für eine feste Zahlung oder für viele Käufer konfigurieren." },
      { icon: ShoppingCart, title: "Vordefinierte Beträge", desc: "Fester Betrag oder vom Kunden wählbar, inklusive Beschreibung." },
      { icon: ShieldCheck, title: "Ablauf & Limits", desc: "Gültigkeit, Nutzungslimits und Zahlungsmethoden pro Link steuern." },
    ],
    architecture: {
      caption: "Payment Link als leichte Checkout-Hülle",
      layers: [
        { label: "Erstellung", nodes: [{ label: "API", sub: "v1" }, { label: "Dashboard", sub: "UI" }] },
        { label: "Link-Layer", nodes: [{ label: "Konfiguration", sub: "Betrag" }, { label: "QR/URL", sub: "teilbar" }, { label: "Limits", sub: "Ablauf" }] },
        { label: "Einlösung", nodes: [{ label: "Checkout", sub: "gehostet" }, { label: "Ledger", sub: "Buchung" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/payment_links",
      code: [
        "// Bezahllink für eine Rechnung erstellen",
        "const link = await cashfux.paymentLinks.create({",
        "  amount: 29900,",
        '  currency: "EUR",',
        '  description: "Rechnung 2025-0421",',
        "  reusable: false,",
        "});",
        "",
        "link.url // https://pay.cashfux.com/l/9fZ2",
      ],
      highlights: [
        "QR-Code automatisch generiert",
        "Ein- oder Mehrfachnutzung wählbar",
        "Webhook payment_link.paid",
      ],
    },
    useCases: [
      { title: "Dienstleister", desc: "Rechnung mit Bezahllink statt Überweisungsträger versenden." },
      { title: "Social Commerce", desc: "Verkaufen über Chat und Social ohne Shop-Setup." },
      { title: "Vor-Ort-Kassieren", desc: "QR-Code am POS ohne Terminal-Hardware." },
    ],
    faq: [
      { q: "Brauche ich einen eigenen Checkout?", a: "Nein. Payment Links nutzen den gehosteten CashFUX-Checkout und funktionieren ohne eigenes Frontend." },
      { q: "Kann ein Link mehrfach verwendet werden?", a: "Ja, Links lassen sich als Einmal- oder Mehrfach-Link mit Nutzungslimit konfigurieren." },
      { q: "Lässt sich ein Ablaufdatum setzen?", a: "Ja, Gültigkeit, Limits und erlaubte Zahlungsmethoden sind pro Link steuerbar." },
      { q: "Wie erfahre ich von der Zahlung?", a: "Über den Webhook payment_link.paid und den Zahlungsstatus im Dashboard." },
    ],
    related: ["checkout", "rechnungen", "zahlungen"],
  },
  {
    slug: "split-payments",
    name: "Split Payments",
    category: CATEGORY,
    icon: Split,
    tagline: "Zahlungen automatisch aufteilen.",
    overline: "Split Payments",
    hero: {
      title: "Eine Zahlung, viele Empfänger.",
      subtitle:
        "Teilen Sie eingehende Zahlungen regelbasiert auf Plattform, Verkäufer und Partner auf – atomar im Ledger, ohne manuelle Verrechnung.",
    },
    stats: [
      { value: "Atomic", label: "Split-Buchung" },
      { value: "Fee", label: "Plattform-Gebühr" },
      { value: "N-Wege", label: "Aufteilung" },
    ],
    features: [
      { icon: Split, title: "Regelbasierte Splits", desc: "Prozentual oder als Festbetrag auf beliebig viele Empfänger verteilen." },
      { icon: Network, title: "Atomare Verbuchung", desc: "Der komplette Split wird in einer einzigen Ledger-Transaktion gebucht." },
      { icon: ShieldCheck, title: "Plattform-Gebühr", desc: "Ihre Provision wird automatisch einbehalten und verbucht." },
      { icon: RefreshCw, title: "Verzögerte Freigabe", desc: "Splits erst nach Bedingung wie Lieferbestätigung ausführen." },
    ],
    architecture: {
      caption: "Split-Regel zwischen Zahlungseingang und Empfängern",
      layers: [
        { label: "Eingang", nodes: [{ label: "Payment", sub: "Kunde" }] },
        { label: "Split-Engine", nodes: [{ label: "Regel", sub: "% / fix" }, { label: "Fee", sub: "Plattform" }, { label: "Holds", sub: "Bedingung" }] },
        { label: "Empfänger", nodes: [{ label: "Seller", sub: "Ledger" }, { label: "Partner", sub: "Ledger" }, { label: "Plattform", sub: "Ledger" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/payments/:id/splits",
      code: [
        "// Zahlung auf mehrere Empfänger aufteilen",
        "const split = await cashfux.splits.create({",
        '  payment: "pay_7a1",',
        "  allocations: [",
        '    { account: "acc_seller", amount: 9000 },',
        '    { account: "acc_platform", amount: 1000 },',
        "  ],",
        "});",
      ],
      highlights: [
        "Prozentuale oder fixe Allokation",
        "Atomar – kein Teil-Split möglich",
        "Webhook split.settled je Empfänger",
      ],
    },
    useCases: [
      { title: "Marktplätze", desc: "Kaufbetrag automatisch zwischen Verkäufer und Plattform aufteilen." },
      { title: "Affiliate-Modelle", desc: "Provisionen sofort an Vermittler ausschütten." },
      { title: "Kollaborationen", desc: "Erlöse zwischen mehreren Beteiligten regelbasiert verteilen." },
    ],
    faq: [
      { q: "Wie werden Splits verbucht?", a: "Atomar: Der gesamte Split ist eine einzige, unteilbare Ledger-Transaktion." },
      { q: "Kann ich meine Plattform-Gebühr einbehalten?", a: "Ja, die Provision wird als eigene Allokation automatisch verbucht." },
      { q: "Sind bedingte Auszahlungen möglich?", a: "Ja, über Holds lassen sich Splits an Bedingungen wie eine Lieferbestätigung koppeln." },
      { q: "Wie viele Empfänger sind möglich?", a: "Beliebig viele – prozentual, fix oder gemischt." },
    ],
    related: ["connected-accounts", "auszahlungen", "zahlungen"],
  },
  {
    slug: "auszahlungen",
    name: "Auszahlungen",
    category: CATEGORY,
    icon: Send,
    tagline: "Massen- und Einzelauszahlungen mit Freigabe.",
    overline: "Auszahlungen",
    hero: {
      title: "Auszahlungen im Maßstab, mit Kontrolle.",
      subtitle:
        "Vom Einzeltransfer bis zur Massenauszahlung: Freigabe-Workflows, Reconciliation und lückenlose Nachvollziehbarkeit über eine API.",
    },
    stats: [
      { value: "Batch", label: "Tausende auf einmal" },
      { value: "Freigabe", label: "Vier-Augen" },
      { value: "Instant", label: "wo verfügbar" },
    ],
    features: [
      { icon: Send, title: "Massenauszahlung", desc: "Tausende Empfänger in einem Batch – validiert, freigegeben, ausgeführt." },
      { icon: ShieldCheck, title: "Freigabe-Workflows", desc: "Vier-Augen-Prinzip, Limits und Rollen vor jeder Ausführung." },
      { icon: RefreshCw, title: "Reconciliation", desc: "Automatischer Abgleich zwischen Auftrag, Buchung und Bankstatus." },
      { icon: Network, title: "Instant wo möglich", desc: "SCT Instant für sekundenschnelle Gutschrift beim Empfänger." },
    ],
    architecture: {
      caption: "Auszahlungs-Batch mit Freigabe und Reconciliation",
      layers: [
        { label: "Auftrag", nodes: [{ label: "Batch", sub: "Datei/API" }, { label: "Validierung", sub: "IBAN/AML" }] },
        { label: "Kontrolle", nodes: [{ label: "Freigabe", sub: "4-Augen" }, { label: "Limits", sub: "Policy" }] },
        { label: "Ausführung", nodes: [{ label: "Rails", sub: "SEPA/Instant" }, { label: "Reconciliation", sub: "Abgleich" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/payouts/batches",
      code: [
        "// Massenauszahlung anlegen (freigabepflichtig)",
        "const batch = await cashfux.payouts.createBatch({",
        '  from: "acc_9f2",',
        "  items: [",
        '    { iban: "DE22...", amount: 50000, ref: "INV-01" },',
        '    { iban: "FR14...", amount: 32000, ref: "INV-02" },',
        "  ],",
        "});",
      ],
      highlights: [
        "Batch bleibt bis Freigabe pending",
        "IBAN- & AML-Validierung vorab",
        "Webhook payout.settled je Item",
      ],
    },
    useCases: [
      { title: "Marktplatz-Payouts", desc: "Verkäufererlöse geplant und automatisiert auszahlen." },
      { title: "Gehälter & Honorare", desc: "Wiederkehrende Zahlungen an viele Empfänger." },
      { title: "Erstattungen", desc: "Kundenrückzahlungen schnell und nachvollziehbar." },
    ],
    faq: [
      { q: "Wie groß dürfen Batches sein?", a: "Batches skalieren auf Tausende Empfänger; jedes Item wird einzeln validiert und abgeglichen." },
      { q: "Sind Freigaben verpflichtend?", a: "Freigabe-Workflows sind konfigurierbar, inklusive Vier-Augen-Prinzip und Limits." },
      { q: "Wie schnell erreicht das Geld den Empfänger?", a: "Bei SCT Instant in Sekunden, sonst gemäß SEPA-Standardlaufzeit." },
      { q: "Wie funktioniert die Abstimmung?", a: "Auftrag, Ledger-Buchung und Bankstatus werden automatisch je Item abgeglichen." },
    ],
    related: ["zahlungen", "split-payments", "treasury"],
  },
  {
    slug: "subscriptions",
    name: "Subscriptions",
    category: CATEGORY,
    icon: RefreshCw,
    tagline: "Wiederkehrende Zahlungen und Abo-Logik.",
    overline: "Subscriptions",
    hero: {
      title: "Abrechnung, die mitwächst.",
      subtitle:
        "Modellieren Sie Pläne, Testphasen, Mengenpreise und Zyklen – mit automatischem Einzug, Dunning und lückenloser Ledger-Wahrheit.",
    },
    stats: [
      { value: "Flexibel", label: "Preismodelle" },
      { value: "Dunning", label: "Fehlversuche" },
      { value: "Proration", label: "anteilig" },
    ],
    features: [
      { icon: RefreshCw, title: "Pläne & Zyklen", desc: "Monatlich, jährlich oder nutzungsbasiert – mit Testphasen und Rabatten." },
      { icon: ArrowLeftRight, title: "Automatischer Einzug", desc: "Wiederkehrende Zahlungen per SEPA-Lastschrift oder Karte." },
      { icon: ShieldCheck, title: "Dunning", desc: "Intelligente Wiederholungslogik bei fehlgeschlagenen Zahlungen." },
      { icon: Split, title: "Proration", desc: "Anteilige Berechnung bei Upgrades, Downgrades und Wechseln." },
    ],
    architecture: {
      caption: "Abrechnungszyklus von Plan bis Ledger",
      layers: [
        { label: "Modell", nodes: [{ label: "Plan", sub: "Preis" }, { label: "Trial", sub: "Phase" }, { label: "Rabatt", sub: "Coupon" }] },
        { label: "Billing-Engine", nodes: [{ label: "Zyklus", sub: "Scheduler" }, { label: "Einzug", sub: "Zahlung" }, { label: "Dunning", sub: "Retry" }] },
        { label: "Ergebnis", nodes: [{ label: "Rechnung", sub: "Beleg" }, { label: "Ledger", sub: "Buchung" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/subscriptions",
      code: [
        "// Abonnement für einen Kunden anlegen",
        "const sub = await cashfux.subscriptions.create({",
        '  customer: "cus_31a",',
        '  plan: "pro_monthly",',
        "  trial_days: 14,",
        "});",
        "",
        'sub.status // "trialing" → "active"',
      ],
      highlights: [
        "Proration bei Plan-Wechsel automatisch",
        "Dunning-Regeln konfigurierbar",
        "Webhook invoice.paid je Zyklus",
      ],
    },
    useCases: [
      { title: "SaaS-Abos", desc: "Monats- und Jahrespläne inklusive Testphasen." },
      { title: "Mitgliedschaften", desc: "Wiederkehrende Beiträge mit automatischem Einzug." },
      { title: "Usage-Billing", desc: "Nutzungsbasierte Abrechnung am Ende jedes Zyklus." },
    ],
    faq: [
      { q: "Welche Preismodelle sind möglich?", a: "Feste, gestaffelte und nutzungsbasierte Preise mit Testphasen und Rabatten." },
      { q: "Was passiert bei einer fehlgeschlagenen Zahlung?", a: "Die Dunning-Logik wiederholt den Einzug nach konfigurierbaren Regeln und benachrichtigt den Kunden." },
      { q: "Wie werden Plan-Wechsel abgerechnet?", a: "Über Proration – anteilig auf den verbleibenden Zyklus berechnet." },
      { q: "Werden Rechnungen erzeugt?", a: "Ja, je Zyklus wird ein Beleg erstellt und im Ledger verbucht." },
    ],
    related: ["rechnungen", "checkout", "buchhaltung"],
  },
];
